import { useState, useEffect } from 'react';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  Globe, CheckCircle, Clock, AlertCircle, XCircle, RefreshCw, Search,
  ShoppingCart, Server, Mail, ChevronDown, ChevronRight,
  Wallet, ShieldCheck, X, Eye, Shield, Lock,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;



const STATUS_CONFIG = {
  active:               { label: 'Active',          color: 'bg-green-100 text-green-700',   icon: CheckCircle },
  verified:             { label: 'Verified',         color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  pending_verification: { label: 'Pending DNS',      color: 'bg-amber-100 text-amber-700',   icon: Clock },
  not_set:              { label: 'Not Configured',   color: 'bg-slate-100 text-slate-500',   icon: XCircle },
};
const TYPE_LABELS = {
  subdomain: { label: 'Subdomain',     color: 'bg-blue-100 text-blue-700' },
  custom:    { label: 'Custom Domain', color: 'bg-violet-100 text-violet-700' },
  bought:    { label: 'Purchased',     color: 'bg-amber-100 text-amber-700' },
  none:      { label: 'None',          color: 'bg-slate-100 text-slate-400' },
};

function fmt(n) {
  return '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}
function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Invoice line-items card for a registered domain request. */
function InvoiceLineItemsCard({ reqId, domain, years }) {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/domain/requests/${reqId}/invoice`)
      .then(r => setInvoice(r.data))
      .catch(() => setInvoice(null))
      .finally(() => setLoading(false));
  }, [reqId]);

  if (loading) return (
    <div className="w-full mt-3 flex items-center gap-2 text-slate-400 text-xs">
      <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Loading invoice…
    </div>
  );
  if (!invoice) return null;

  const items = invoice.line_items || [];
  return (
    <div className="w-full mt-3" data-testid={`invoice-card-${reqId}`}>
      <p className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-2 flex items-center gap-1.5">
        <ShieldCheck className="w-3.5 h-3.5 text-blue-600" />
        Invoice #{invoice.invoice_number}
        <span className="text-[10px] font-bold bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full ml-1">PAID</span>
      </p>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-100">
              <th className="px-4 py-2 text-left font-semibold text-slate-600">Item</th>
              <th className="px-4 py-2 text-left font-semibold text-slate-500">Details</th>
              <th className="px-4 py-2 text-right font-semibold text-slate-600">Total</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-4 py-3 text-slate-400 italic">No line items recorded</td>
              </tr>
            ) : items.map((item, i) => (
              <tr key={i} className="border-b border-slate-50">
                <td className="px-4 py-2.5 font-medium text-slate-700">{item.name}</td>
                <td className="px-4 py-2.5 text-slate-400">{item.detail || '—'}</td>
                <td className="px-4 py-2.5 text-right font-semibold text-slate-800">{fmt(item.total)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-blue-600">
              <td colSpan={2} className="px-4 py-2.5 text-sm font-bold text-blue-100">Total Charged</td>
              <td className="px-4 py-2.5 text-right text-sm font-extrabold text-white">{fmt(invoice.total_amount)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

export default function AdminDomainManager() {
  const [activeTab, setActiveTab] = useState('domainRequests');

  // ── Domain Requests state ─────────────────────────────────────────────────
  const [domainRequests, setDomainRequests] = useState([]);
  const [reqLoading, setReqLoading] = useState(false);
  const [approving, setApproving] = useState('');
  const [rejectDialog, setRejectDialog] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [expandedReqId, setExpandedReqId] = useState(null);
  const [viewingAdminReq, setViewingAdminReq] = useState(null);

  // ── Approve Dialog with Wallet Deduction Preview ──────────────────────────
  const [approveDialog, setApproveDialog]       = useState(null); // holds the request object
  const [domainSettings, setDomainSettings]     = useState(null);
  const [walletBalance, setWalletBalance]       = useState(null);

  // ── Email provisioning state ───────────────────────────────────────────────
  const [provisionForms, setProvisionForms] = useState({}); // keyed by req.id
  const [provisioning, setProvisioning] = useState(''); // req id currently being provisioned

  // ── White-label domains state ────────────────────────────────────────────
  const [domains, setDomains] = useState([]);
  const [domainsLoading, setDomainsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // ── Domain registrations state ────────────────────────────────────────────
  const [registrations, setRegistrations] = useState([]);
  const [regLoading, setRegLoading] = useState(false);
  const [regSearch, setRegSearch] = useState('');

  // ── Server IP state ───────────────────────────────────────────────────────
  const [serverIp, setServerIp] = useState(null);
  const [ipLoading, setIpLoading] = useState(false);

  // ── Fetch domain requests ─────────────────────────────────────────────────
  const fetchDomainRequests = async () => {
    setReqLoading(true);
    try {
      const res = await axios.get(`${API}/domain/requests`);
      setDomainRequests(res.data || []);
    } catch { toast.error('Failed to load domain requests'); }
    finally { setReqLoading(false); }
  };

  const handleApproveRequest = async (reqId) => {
    setApproving(reqId);
    try {
      const res = await axios.post(`${API}/domain/requests/${reqId}/approve`, { notes: adminNotes });
      if (res.data?.status === 'payment_pending') {
        toast.warning(res.data.message || 'Insufficient balance — payment pending notification sent');
      } else {
        toast.success(res.data.message || 'Domain registered successfully!');
      }
      setApproveDialog(null);
      fetchDomainRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approval failed');
    } finally {
      setApproving('');
    }
  };

  const openApproveDialog = async (req) => {
    setApproveDialog(req);
    // Fetch admin domain settings for deduction preview (if not already loaded)
    if (!domainSettings) {
      try {
        const [settingsRes, walletRes] = await Promise.all([
          axios.get(`${API}/admin/domain-settings`),
          axios.get(`${API}/wallet/balance`),
        ]);
        setDomainSettings(settingsRes.data);
        setWalletBalance(walletRes.data?.balance ?? null);
      } catch { /* silent — show N/A */ }
    }
  };

  const handleRejectRequest = async () => {
    if (!rejectDialog) return;
    try {
      await axios.post(`${API}/domain/requests/${rejectDialog.id}/reject`, { reason: rejectReason });
      toast.success('Request rejected');
      setRejectDialog(null);
      setRejectReason('');
      fetchDomainRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reject failed');
    }
  };

  const handleProvisionEmail = async (reqId) => {
    const form = provisionForms[reqId] || {};
    setProvisioning(reqId);
    try {
      const res = await axios.post(`${API}/domain/requests/${reqId}/provision-email`, form);
      toast.success(res.data.message || 'Email hosting provisioned!');
      fetchDomainRequests();
      setProvisionForms(prev => ({ ...prev, [reqId]: {} }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Provisioning failed');
    } finally {
      setProvisioning('');
    }
  };

  const updateProvisionForm = (reqId, field, value) => {
    setProvisionForms(prev => ({
      ...prev,
      [reqId]: { ...(prev[reqId] || {}), [field]: value },
    }));
  };

  // ── White-label domains ───────────────────────────────────────────────────
  const fetchDomains = async () => {
    setDomainsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/white-label/domains`);
      setDomains(res.data || []);
    } catch { toast.error('Failed to load domain data'); }
    finally { setDomainsLoading(false); }
  };

  // ── Fetch domain registrations ────────────────────────────────────────────
  const fetchRegistrations = async () => {
    setRegLoading(true);
    try {
      const res = await axios.get(`${API}/admin/domain-orders`);
      setRegistrations(res.data?.orders || []);
    } catch { toast.error('Failed to load domain registrations'); }
    finally { setRegLoading(false); }
  };

  // ── Fetch server IP ───────────────────────────────────────────────────────
  const fetchServerIp = async () => {
    setIpLoading(true);
    try {
      const res = await axios.get(`${API}/server-ip`);
      setServerIp(res.data?.outbound_ip || 'Unknown');
    } catch { setServerIp('Unable to fetch'); }
    finally { setIpLoading(false); }
  };

  useEffect(() => { fetchDomains(); }, []);
  useEffect(() => {
    if (activeTab === 'domainRequests') fetchDomainRequests();
    if (activeTab === 'registrations') fetchRegistrations();
    if (activeTab === 'serverIp') fetchServerIp();
  }, [activeTab]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const filtered = domains.filter(d => {
    const q = search.toLowerCase();
    const matchSearch = !q
      || (d.company_name || '').toLowerCase().includes(q)
      || (d.email || '').toLowerCase().includes(q)
      || (d.subdomain || '').toLowerCase().includes(q)
      || (d.custom_domain || '').toLowerCase().includes(q);
    const matchStatus = statusFilter === 'all' || d.domain_status === statusFilter;
    return matchSearch && matchStatus;
  });

  const filteredReg = registrations.filter(o => {
    const q = regSearch.toLowerCase();
    return !q
      || (o.domain || '').toLowerCase().includes(q)
      || (o.partner_name || '').toLowerCase().includes(q)
      || (o.client_name || '').toLowerCase().includes(q)
      || (o.partner_email || '').toLowerCase().includes(q);
  });

  const stats = {
    total:   domains.length,
    active:  domains.filter(d => d.domain_status === 'active' || d.domain_status === 'verified').length,
    pending: domains.filter(d => d.domain_status === 'pending_verification').length,
    custom:  domains.filter(d => d.domain_type === 'custom' || d.domain_type === 'bought').length,
  };

  const TABS = [
    { key: 'domainRequests',    label: 'Domain Requests',   icon: Clock,
      badge: domainRequests.filter(r => r.status === 'pending_admin').length || undefined },
    { key: 'whiteLabelDomains', label: 'White Label Domains', icon: Globe },
    { key: 'registrations',     label: 'Domain Registrations', icon: ShoppingCart,
      badge: registrations.length },
    { key: 'serverIp',          label: 'Server IP (RC Whitelist)', icon: Server },
  ];

  return (
    <DashboardLayout role="admin">
      <div className="space-y-6">

        {/* Page Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-4xl font-bold tracking-tight" data-testid="domain-manager-title">
              Domain Manager
            </h1>
            <p className="text-slate-500 mt-2">Monitor partner domains, registrations &amp; ResellerClub IP whitelist</p>
          </div>
          <Button
            variant="outline"
            onClick={activeTab === 'registrations' ? fetchRegistrations : activeTab === 'serverIp' ? fetchServerIp : fetchDomains}
            disabled={domainsLoading || regLoading || ipLoading}
            data-testid="refresh-domains-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${(domainsLoading || regLoading || ipLoading) ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Total Partners', value: stats.total,   icon: Globe,        color: 'text-blue-600',   bg: 'bg-blue-50' },
            { label: 'Live Domains',   value: stats.active,  icon: CheckCircle,  color: 'text-green-600',  bg: 'bg-green-50' },
            { label: 'Pending DNS',    value: stats.pending, icon: Clock,        color: 'text-amber-600',  bg: 'bg-amber-50' },
            { label: 'Registrations',  value: registrations.length, icon: ShoppingCart, color: 'text-violet-600', bg: 'bg-violet-50' },
          ].map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4">
                <div className={`${s.bg} p-2.5 rounded-lg`}>
                  <Icon className={`h-5 w-5 ${s.color}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{s.value}</p>
                  <p className="text-xs text-slate-500">{s.label}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200">
          <div className="flex gap-1">
            {TABS.map(({ key, label, icon: Icon, badge }) => (
              <button
                key={key}
                data-testid={`admin-tab-${key}`}
                onClick={() => setActiveTab(key)}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors
                  ${activeTab === key
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'}`}
              >
                <Icon className="w-4 h-4" />
                {label}
                {badge > 0 && (
                  <span className="bg-blue-100 text-blue-700 text-xs px-1.5 py-0.5 rounded-full">{badge}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* ═══ WHITE LABEL DOMAINS ═══ */}
        {/* ══ DOMAIN REQUESTS TAB ══ */}
        {activeTab === 'domainRequests' && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b bg-slate-50 flex items-center justify-between">
              <h2 className="font-semibold text-slate-800">Domain Registration Requests</h2>
              <Button size="sm" variant="outline" onClick={fetchDomainRequests} className="gap-1 text-xs">
                <RefreshCw className="w-3.5 h-3.5" /> Refresh
              </Button>
            </div>

            {/* Admin Request Details Modal */}
            {viewingAdminReq && (() => {
              const r = viewingAdminReq;
              const isQuoted    = (r.quoted_total || 0) > 0;
              const domainBase  = isQuoted ? (r.quoted_domain_price || 0) : Math.max(0, (r.price || 0) - (r.whois_addon_price || 0));
              const whoisCost   = isQuoted ? (r.quoted_whois_cost || 0) : (r.whois_addon_price || 0);
              const emailCost   = isQuoted ? (r.quoted_email_cost || 0) : (r.email_cost_estimate || 0);
              const dpCost      = isQuoted ? (r.quoted_protector_cost || 0) : (r.domain_protector_cost || 0);
              const totalAmount = isQuoted ? r.quoted_total : (r.price || 0) + emailCost + dpCost;
              const sMap = {
                pending_sp: 'Pending SP', pending_cp: 'Pending CP',
                pending_admin: 'Pending Admin', payment_pending: 'Payment Pending',
                registered: 'Registered', rejected: 'Rejected',
              };
              return (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                  <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto" data-testid="admin-req-details-modal">
                    <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                      <div>
                        <h2 className="text-base font-semibold text-slate-800 font-mono">{r.domain}</h2>
                        <p className="text-xs text-slate-400 mt-0.5">Admin — Wholesale Breakdown</p>
                      </div>
                      <button onClick={() => setViewingAdminReq(null)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600" data-testid="close-admin-req-details">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="px-6 py-5 space-y-5">
                      <div className="flex flex-wrap gap-2">
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${r.status === 'registered' ? 'bg-green-100 text-green-700' : r.status === 'rejected' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700'}`}>
                          {sMap[r.status] || r.status}
                        </span>
                        <span className="text-xs text-slate-400">{r.years} year{r.years !== 1 ? 's' : ''}</span>
                        {r.domain_protector && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 flex items-center gap-1"><Lock className="w-3 h-3" /> Domain Protector+</span>}
                        {r.whois_privacy && <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 flex items-center gap-1"><Shield className="w-3 h-3" /> WHOIS Privacy</span>}
                      </div>
                      <div className="text-sm">
                        <span className="text-xs text-slate-400 block mb-0.5">Requested By</span>
                        <span className="font-medium text-slate-800">{r.requested_by_name}</span>
                        {r.requested_by_role && <span className="ml-2 text-xs text-slate-400 capitalize">({r.requested_by_role.replace('_', ' ')})</span>}
                      </div>
                      {!r.email_only && (
                        <div>
                          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">{isQuoted ? 'Price Breakdown' : 'Wholesale Breakdown'}</p>
                          <div className="bg-slate-50 rounded-xl border border-slate-200 divide-y divide-slate-100">
                            <div className="flex justify-between px-4 py-2.5 text-sm">
                              <span className="text-slate-600">Domain ({r.years}yr)</span>
                              <span className="font-semibold text-slate-800">{fmt(domainBase)}</span>
                            </div>
                            {whoisCost > 0 && (
                              <div className="flex justify-between px-4 py-2.5 text-sm">
                                <span className="text-slate-600 flex items-center gap-1"><Shield className="w-3.5 h-3.5 text-slate-400" /> WHOIS Privacy</span>
                                <span className="font-semibold text-slate-800">{fmt(whoisCost)}</span>
                              </div>
                            )}
                            {emailCost > 0 && (
                              <div className="flex justify-between px-4 py-2.5 text-sm">
                                <span className="text-slate-600 flex items-center gap-1"><Mail className="w-3.5 h-3.5 text-violet-500" /> Email ({r.email_accounts_count} acc × {r.years}yr)</span>
                                <span className="font-semibold text-violet-700">{fmt(emailCost)}</span>
                              </div>
                            )}
                            {dpCost > 0 && (
                              <div className="flex justify-between px-4 py-2.5 text-sm">
                                <span className="text-slate-600 flex items-center gap-1"><Lock className="w-3.5 h-3.5 text-blue-600" /> Domain Protector+</span>
                                <span className="font-semibold text-blue-700">{fmt(dpCost)}</span>
                              </div>
                            )}
                            {isQuoted && (
                              <>
                                <div className="flex justify-between px-4 py-2.5 text-sm">
                                  <span className="text-slate-600">Subtotal (excl. GST)</span>
                                  <span className="font-semibold text-slate-800">{fmt(r.quoted_subtotal || 0)}</span>
                                </div>
                                <div className="flex justify-between px-4 py-2.5 text-sm">
                                  <span className="text-slate-600">GST (18% IGST)</span>
                                  <span className="font-semibold text-slate-800">{fmt(r.quoted_gst || 0)}</span>
                                </div>
                              </>
                            )}
                            <div className="flex justify-between px-4 py-3 bg-blue-50 rounded-b-xl">
                              <span className="text-sm font-bold text-slate-700">{isQuoted ? 'Total (incl. GST)' : 'Total (Wholesale)'}</span>
                              <span className="text-sm font-bold text-blue-700">{fmt(totalAmount)}</span>
                            </div>
                          </div>
                        </div>
                      )}
                      {r.notes && (
                        <div>
                          <p className="text-xs text-slate-400 uppercase tracking-wide mb-1.5">Notes</p>
                          <div className="bg-slate-50 rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-600">{r.notes}</div>
                        </div>
                      )}
                      {r.status === 'rejected' && r.rejection_reason && (
                        <div className="bg-red-50 rounded-xl border border-red-100 px-4 py-3 text-sm text-red-700">{r.rejection_reason}</div>
                      )}
                      <p className="text-xs text-slate-400 text-right">Submitted: {r.created_at ? new Date(r.created_at).toLocaleString('en-IN') : '—'}</p>
                    </div>
                  </div>
                </div>
              );
            })()}
            {reqLoading ? (
              <div className="flex justify-center py-14"><RefreshCw className="w-5 h-5 animate-spin text-slate-300" /></div>
            ) : domainRequests.length === 0 ? (
              <div className="text-center py-14">
                <Globe className="w-10 h-10 text-slate-200 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">No domain requests yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b text-left">
                      {['','Domain','From','Years','Total Price','Submitted','Status','Action'].map(h=>(
                        <th key={h} className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {domainRequests.map(r => {
                      const sMap = {
                        pending_sp:      { label:'Pending SP',    cls:'bg-amber-100 text-amber-700' },
                        pending_cp:      { label:'Pending CP',    cls:'bg-amber-100 text-amber-700' },
                        pending_admin:   { label:'Pending Admin', cls:'bg-blue-100 text-blue-700' },
                        payment_pending: { label:'Payment Pending', cls:'bg-red-100 text-red-600' },
                        registered:      { label:'Registered',    cls:'bg-green-100 text-green-700' },
                        rejected:        { label:'Rejected',      cls:'bg-red-100 text-red-600' },
                      };
                      const s = sMap[r.status] || { label: r.status, cls: 'bg-slate-100 text-slate-500' };
                      const canApprove = r.status === 'pending_admin' || r.status === 'payment_pending';
                      const isExpanded = expandedReqId === r.id;
                      const hasEmail   = r.email_hosting_requested || r.email_only;

                      return (
                        <>
                          <tr
                            key={r.id}
                            className={`hover:bg-slate-50 cursor-pointer ${isExpanded ? 'bg-blue-50/40' : ''}`}
                            data-testid={`req-row-${r.id}`}
                            onClick={() => setExpandedReqId(isExpanded ? null : r.id)}
                          >
                            <td className="px-4 py-3 text-slate-400">
                              {isExpanded
                                ? <ChevronDown className="w-4 h-4 text-blue-500" />
                                : <ChevronRight className="w-4 h-4" />}
                            </td>
                            <td className="px-4 py-3">
                              <span className="font-mono font-semibold text-slate-800">{r.domain}</span>
                              {r.email_only && (
                                <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 font-semibold">EMAIL ONLY</span>
                              )}
                              {!r.email_only && hasEmail && (
                                <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-semibold flex-shrink-0 inline-flex items-center gap-0.5">
                                  <Mail className="w-2.5 h-2.5" />+Email
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-xs text-slate-600">{r.requested_by_name}</td>
                            <td className="px-4 py-3 text-slate-600">{r.years}yr</td>
                            <td className="px-4 py-3 font-semibold text-blue-700">
                              {r.email_only ? '—' : (() => {
                                const isQuoted = (r.quoted_total || 0) > 0;
                                const total = isQuoted
                                  ? r.quoted_total
                                  : (r.price || 0) + (r.email_cost_estimate || 0) + (r.domain_protector_cost || 0);
                                return (
                                  <div>
                                    <span>{fmt(total)}</span>
                                    {isQuoted ? (
                                      <p className="text-[10px] text-slate-400 font-normal">incl. addons + GST</p>
                                    ) : ((r.email_cost_estimate || 0) + (r.domain_protector_cost || 0)) > 0 && (
                                      <p className="text-[10px] text-slate-400 font-normal">incl. addons</p>
                                    )}
                                  </div>
                                );
                              })()}
                            </td>
                            <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(r.created_at)}</td>
                            <td className="px-4 py-3">
                              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${s.cls}`}>{s.label}</span>
                            </td>
                            <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                              <div className="flex items-center gap-2">
                                <button
                                  data-testid={`admin-view-req-${r.id}`}
                                  onClick={() => setViewingAdminReq(r)}
                                  className="p-1.5 rounded-lg border border-slate-200 text-slate-400 hover:text-blue-600 hover:border-blue-200 hover:bg-blue-50 transition-colors"
                                  title="View full breakdown"
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                </button>
                                {canApprove ? (
                                  <>
                                    <Button size="sm"
                                      data-testid={`admin-approve-${r.id}`}
                                      disabled={approving === r.id}
                                      onClick={() => openApproveDialog(r)}
                                      className="bg-green-600 hover:bg-green-700 text-white text-xs h-7 px-3">
                                      {approving === r.id ? <RefreshCw className="w-3 h-3 animate-spin" /> : (r.email_only ? 'Approve' : 'Approve & Register')}
                                    </Button>
                                    <Button size="sm" variant="outline"
                                      data-testid={`admin-reject-${r.id}`}
                                      onClick={() => { setRejectDialog(r); setRejectReason(''); }}
                                      className="text-red-600 border-red-200 text-xs h-7 px-3 hover:border-red-600 hover:bg-red-600">
                                      Reject
                                    </Button>
                                  </>
                                ) : null}
                              </div>
                            </td>
                          </tr>

                          {/* Expanded row: email details + provisioning + notes */}
                          {isExpanded && (
                            <tr key={`${r.id}-exp`} className="bg-blue-50/30">
                              <td colSpan={8} className="px-6 py-4">
                                <div className="flex flex-wrap gap-6">
                                  {/* Email Hosting Details */}
                                  {hasEmail ? (
                                    <div className="flex-1 min-w-[220px]">
                                      <p className="text-xs font-semibold text-violet-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                                        <Mail className="w-3.5 h-3.5" />
                                        Email Hosting Request
                                      </p>
                                      <div className="bg-white rounded-xl border border-violet-100 px-4 py-3 text-sm space-y-1.5">
                                        <div className="flex items-center gap-2">
                                          <span className="text-slate-500 text-xs">Accounts:</span>
                                          <span className="font-semibold text-slate-800">
                                            {r.email_accounts_count > 0 ? `${r.email_accounts_count} account(s)` : 'Not specified'}
                                          </span>
                                        </div>
                                        {/* Email Duration Badge */}
                                        {r.years > 0 && (
                                          <div className="flex items-center gap-2 mt-1">
                                            <span className="text-slate-500 text-xs">Duration:</span>
                                            <span className="inline-flex items-center gap-1 text-[11px] font-bold bg-blue-600 text-white px-2.5 py-1 rounded-full">
                                              <Mail className="w-3 h-3" />
                                              {r.years} Year{r.years > 1 ? 's' : ''} Email Hosting Included
                                            </span>
                                          </div>
                                        )}
                                        {r.email_usernames && r.email_usernames.length > 0 && (
                                          <div>
                                            <p className="text-xs text-slate-500 mb-1">Requested usernames:</p>
                                            <div className="flex flex-wrap gap-1.5">
                                              {r.email_usernames.map((u, i) => (
                                                <span key={i} className="text-xs bg-violet-50 border border-violet-200 text-violet-700 rounded-full px-2.5 py-0.5 font-mono">
                                                  {u}@{r.domain}
                                                </span>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="flex-1 min-w-[220px]">
                                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Email Hosting</p>
                                      <p className="text-sm text-slate-400 italic">No email hosting requested with this domain.</p>
                                    </div>
                                  )}

                                  {/* Notes */}
                                  {r.notes && (
                                    <div className="flex-1 min-w-[200px]">
                                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Notes from Requester</p>
                                      <div className="bg-white rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-600">
                                        {r.notes}
                                      </div>
                                    </div>
                                  )}

                                  {/* Domain Security */}
                                  {!r.email_only && (
                                    <div className="flex-1 min-w-[180px]">
                                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Domain Security</p>
                                      <div className="bg-white rounded-xl border border-slate-200 px-4 py-3 space-y-2">
                                        <div className="flex items-center gap-2 text-sm">
                                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${r.domain_protector ? 'bg-green-500' : 'bg-slate-300'}`} />
                                          <span className={r.domain_protector ? 'text-slate-700 font-medium' : 'text-slate-400'}>Domain Protector+</span>
                                          {r.domain_protector
                                            ? <span className="text-[10px] bg-blue-50 text-blue-700 border border-blue-100 rounded-full px-1.5 font-medium">₹{r.domain_protector_cost || 0}</span>
                                            : <span className="text-[10px] bg-slate-50 text-slate-400 border border-slate-200 rounded-full px-1.5">Not Added</span>}
                                        </div>
                                        <div className="flex items-center gap-2 text-sm">
                                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${r.whois_privacy !== false ? 'bg-green-500' : 'bg-slate-300'}`} />
                                          <span className={r.whois_privacy !== false ? 'text-slate-700 font-medium' : 'text-slate-400 line-through'}>WHOIS Privacy</span>
                                          {r.whois_privacy !== false
                                            ? <span className="text-[10px] bg-green-50 text-green-700 border border-green-100 rounded-full px-1.5 font-medium">+₹{r.whois_addon_price || 299}/yr</span>
                                            : <span className="text-[10px] bg-slate-50 text-slate-400 border border-slate-200 rounded-full px-1.5">Disabled</span>}
                                        </div>
                                        <div className="flex items-center gap-2 text-sm">
                                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${r.ssl_required !== false ? 'bg-green-500' : 'bg-slate-300'}`} />
                                          <span className={r.ssl_required !== false ? 'text-slate-700 font-medium' : 'text-slate-400 line-through'}>SSL Certificate</span>
                                          {r.ssl_required !== false
                                            ? <span className="text-[10px] bg-green-50 text-green-700 border border-green-100 rounded-full px-1.5 font-medium">Free · Let's Encrypt</span>
                                            : <span className="text-[10px] bg-slate-50 text-slate-400 border border-slate-200 rounded-full px-1.5">Disabled</span>}
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {/* Admin notes input (only when can approve) */}
                                  {(r.status === 'pending_admin' || r.status === 'payment_pending') && (
                                    <div className="flex-1 min-w-[200px]">
                                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Admin Notes (optional)</p>
                                      <textarea
                                        rows={2}
                                        placeholder="Add notes before approving..."
                                        value={adminNotes}
                                        onChange={e => setAdminNotes(e.target.value)}
                                        className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-300"
                                      />
                                    </div>
                                  )}

                                  {/* Invoice Line Items — shown for registered requests */}
                                  {r.status === 'registered' && r.invoice_id && (
                                    <InvoiceLineItemsCard reqId={r.id} domain={r.domain} years={r.years} />
                                  )}

                                  {/* Email Provisioning Form — shown for registered requests with email hosting */}
                                  {hasEmail && (r.status === 'registered' || r.status === 'payment_pending' || r.email_only) && (
                                    <div className="w-full mt-2">
                                      <div className="border-t border-violet-100 pt-4">
                                        {r.email_provisioning_status === 'provisioned' ? (
                                          <div className="flex items-center gap-2 text-green-700 bg-green-50 rounded-xl border border-green-100 px-4 py-3">
                                            <CheckCircle className="w-4 h-4 flex-shrink-0" />
                                            <div className="text-sm">
                                              <span className="font-semibold">Email Hosting Provisioned</span>
                                              {r.email_provisioning_details?.smtp_server && (
                                                <span className="ml-2 text-xs text-green-600">Server: {r.email_provisioning_details.smtp_server}</span>
                                              )}
                                            </div>
                                          </div>
                                        ) : (
                                          <div>
                                            <p className="text-xs font-semibold text-violet-700 uppercase tracking-wide mb-3 flex items-center gap-1">
                                              <Mail className="w-3.5 h-3.5" />
                                              Provision Email Hosting
                                            </p>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                              <div>
                                                <label className="text-xs text-slate-500 font-medium block mb-1">Mail Server (SMTP)</label>
                                                <input
                                                  type="text"
                                                  placeholder="mail.example.com"
                                                  value={provisionForms[r.id]?.smtp_server || ''}
                                                  onChange={e => updateProvisionForm(r.id, 'smtp_server', e.target.value)}
                                                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
                                                />
                                              </div>
                                              <div>
                                                <label className="text-xs text-slate-500 font-medium block mb-1">Mail Quota</label>
                                                <input
                                                  type="text"
                                                  placeholder="5GB per account"
                                                  value={provisionForms[r.id]?.mail_quota || ''}
                                                  onChange={e => updateProvisionForm(r.id, 'mail_quota', e.target.value)}
                                                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
                                                />
                                              </div>
                                              <div>
                                                <label className="text-xs text-slate-500 font-medium block mb-1">Initial Password</label>
                                                <input
                                                  type="text"
                                                  placeholder="Temp password"
                                                  value={provisionForms[r.id]?.initial_password || ''}
                                                  onChange={e => updateProvisionForm(r.id, 'initial_password', e.target.value)}
                                                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
                                                />
                                              </div>
                                              <div>
                                                <label className="text-xs text-slate-500 font-medium block mb-1">Notes (optional)</label>
                                                <input
                                                  type="text"
                                                  placeholder="Webmail URL, etc."
                                                  value={provisionForms[r.id]?.notes || ''}
                                                  onChange={e => updateProvisionForm(r.id, 'notes', e.target.value)}
                                                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
                                                />
                                              </div>
                                            </div>
                                            <Button
                                              size="sm"
                                              data-testid={`provision-email-${r.id}`}
                                              disabled={provisioning === r.id}
                                              onClick={() => handleProvisionEmail(r.id)}
                                              className="mt-3 bg-violet-600 hover:bg-violet-700 text-white gap-1.5"
                                            >
                                              {provisioning === r.id
                                                ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                                : <Mail className="w-3.5 h-3.5" />}
                                              {provisioning === r.id ? 'Provisioning...' : 'Mark Email Provisioned & Notify User'}
                                            </Button>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {/* Rejection reason (if rejected) */}
                                  {r.status === 'rejected' && r.rejection_reason && (
                                    <div className="flex-1 min-w-[200px]">
                                      <p className="text-xs font-semibold text-red-500 uppercase tracking-wide mb-2">Rejection Reason</p>
                                      <div className="bg-red-50 rounded-xl border border-red-100 px-4 py-3 text-sm text-red-700">
                                        {r.rejection_reason}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Approve Dialog — Wallet Deduction Preview */}
        {approveDialog && (() => {
          const r         = approveDialog;
          const ds        = domainSettings;
          const emailCost = ds && r.email_hosting_requested
            ? ds.email_price_per_account * (r.email_accounts_count || 0) * (r.years || 1)
            : 0;
          const whoisCost = ds && r.whois_privacy ? ds.whois_privacy_price : 0;
          const dpCost    = r.domain_protector_cost || 0;
          const rcCost    = r.email_only ? 0 : (r.price || 0);
          const total     = rcCost + emailCost + dpCost;

          return (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setApproveDialog(null)}>
              <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>

                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-bold text-slate-800">Confirm Approval</h3>
                    <p className="text-xs text-slate-400 font-mono mt-0.5">{r.domain}</p>
                  </div>
                  <button onClick={() => setApproveDialog(null)} className="text-slate-400 hover:text-slate-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Wallet balance */}
                {walletBalance !== null && (
                  <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 mb-4">
                    <Wallet className="w-4 h-4 text-slate-400" />
                    <span className="text-xs text-slate-500">Wallet Balance:</span>
                    <span className={`text-sm font-bold ml-auto ${walletBalance >= total ? 'text-green-700' : 'text-red-600'}`}>
                      {fmt(walletBalance)}
                    </span>
                    {walletBalance < total && (
                      <span className="text-[10px] font-bold bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full ml-1">INSUFFICIENT</span>
                    )}
                  </div>
                )}

                {/* Cost Breakdown */}
                <div className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden mb-4">
                  <p className="text-xs font-bold text-slate-600 uppercase tracking-wide px-4 py-2.5 border-b border-slate-100 bg-white">
                    Wallet Deduction Breakdown
                  </p>
                  <div className="divide-y divide-slate-100">
                    {!r.email_only && (
                      <div className="flex items-center justify-between px-4 py-2.5 text-sm">
                        <span className="text-slate-600">Domain Registration RC Cost ({r.years}yr)</span>
                        <span className="font-semibold text-slate-800">{fmt(rcCost)}</span>
                      </div>
                    )}
                    {r.email_hosting_requested && (
                      <div className="flex items-center justify-between px-4 py-2.5 text-sm">
                        <span className="text-slate-600">
                          Email Hosting ({r.email_accounts_count || 0} acc × {r.years}yr)
                        </span>
                        <span className="font-semibold text-slate-800">
                          {ds ? fmt(emailCost) : '—'}
                        </span>
                      </div>
                    )}
                    {r.whois_privacy && !r.email_only && (
                      <div className="flex items-center justify-between px-4 py-2.5 text-sm">
                        <span className="text-slate-600">WHOIS Privacy (1yr)</span>
                        <span className="font-semibold text-slate-800">{ds ? fmt(whoisCost) : '—'}</span>
                      </div>
                    )}
                    {r.domain_protector && !r.email_only && dpCost > 0 && (
                      <div className="flex items-center justify-between px-4 py-2.5 text-sm">
                        <span className="text-slate-600 flex items-center gap-1"><Lock className="w-3 h-3 text-blue-600" /> Domain Protector+</span>
                        <span className="font-semibold text-blue-700">{fmt(dpCost)}</span>
                      </div>
                    )}
                    {!r.email_hosting_requested && !r.whois_privacy && r.email_only && (
                      <div className="px-4 py-2.5 text-sm text-slate-400 italic">Email only request — no domain RC cost</div>
                    )}
                  </div>
                  <div className="flex items-center justify-between px-4 py-3 bg-blue-600">
                    <span className="text-sm font-bold text-blue-100">Total Deduction</span>
                    <span className="text-base font-extrabold text-white">{fmt(total)}</span>
                  </div>
                </div>

                {/* Notes */}
                <div className="mb-4">
                  <label className="text-xs font-medium text-slate-600 block mb-1">Admin Notes (optional)</label>
                  <textarea
                    rows={2}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-300"
                    placeholder="Any notes for this approval..."
                    value={adminNotes}
                    onChange={e => setAdminNotes(e.target.value)}
                    data-testid="approve-notes-input"
                  />
                </div>

                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setApproveDialog(null)} className="text-sm">Cancel</Button>
                  <Button
                    onClick={() => handleApproveRequest(r.id)}
                    disabled={approving === r.id}
                    className="bg-green-600 hover:bg-green-700 text-white gap-2 text-sm"
                    data-testid="confirm-approve-btn"
                  >
                    {approving === r.id
                      ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Processing…</>
                      : <><CheckCircle className="w-3.5 h-3.5" /> Confirm Approval</>}
                  </Button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Reject dialog */}
        {rejectDialog && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setRejectDialog(null)}>
            <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
              <h3 className="font-semibold text-slate-800 mb-2">Reject Domain Request</h3>
              <p className="text-sm text-slate-500 mb-3">Domain: <strong>{rejectDialog.domain}</strong></p>
              <label className="text-xs font-medium text-slate-600 block mb-1">Reason</label>
              <textarea
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none min-h-[70px]"
                placeholder="Reason for rejection..."
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
              />
              <div className="flex gap-2 mt-4 justify-end">
                <button className="px-4 py-2 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50" onClick={() => setRejectDialog(null)}>Cancel</button>
                <button className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm hover:bg-red-700" onClick={handleRejectRequest}>Reject</button>
              </div>
            </div>
          </div>
        )}

        {/* ══ WHITE LABEL DOMAINS TAB ══ */}
        {activeTab === 'whiteLabelDomains' && (
          <>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search partner or domain..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="domain-search-input"
                />
              </div>
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="h-10 border border-slate-200 rounded-lg px-3 text-sm bg-white text-slate-700"
                data-testid="domain-status-filter"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="verified">Verified</option>
                <option value="pending_verification">Pending DNS</option>
                <option value="not_set">Not Configured</option>
              </select>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              {domainsLoading ? (
                <div className="flex items-center justify-center h-48">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                </div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-16 text-slate-400">
                  <Globe className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No domains found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="domain-table">
                    <thead>
                      <tr className="border-b border-slate-100 bg-slate-50">
                        {['Partner', 'Domain', 'Type', 'Status', 'Verified At', 'Logo'].map(h => (
                          <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {filtered.map((d, i) => {
                        const statusCfg = STATUS_CONFIG[d.domain_status] || STATUS_CONFIG.not_set;
                        const typeCfg   = TYPE_LABELS[d.domain_type] || TYPE_LABELS.none;
                        const StatusIcon = statusCfg.icon;
                        const domainDisplay = d.domain_type === 'subdomain'
                          ? `${d.subdomain}.cloudsetu.com`
                          : d.custom_domain || '—';
                        return (
                          <tr key={i} className="hover:bg-slate-50 transition-colors" data-testid={`domain-row-${i}`}>
                            <td className="px-5 py-4">
                              <p className={`font-medium ${d.company_name === 'Unknown' ? 'text-slate-400 italic' : 'text-slate-800'}`}>
                                {d.company_name === 'Unknown' ? 'Deleted / Unknown' : d.company_name || '—'}
                              </p>
                              <p className="text-xs text-slate-400 mt-0.5">{d.email}</p>
                            </td>
                            <td className="px-5 py-4">
                              {domainDisplay !== '—'
                                ? <span className="font-mono text-blue-700 text-xs">{domainDisplay}</span>
                                : <span className="text-slate-300 text-xs">—</span>}
                            </td>
                            <td className="px-5 py-4">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${typeCfg.color}`}>
                                {typeCfg.label}
                              </span>
                            </td>
                            <td className="px-5 py-4">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${statusCfg.color}`}>
                                <StatusIcon className="h-3 w-3" />
                                {statusCfg.label}
                              </span>
                            </td>
                            <td className="px-5 py-4 text-xs text-slate-500">
                              {d.domain_verified_at
                                ? fmtDate(d.domain_verified_at)
                                : '—'}
                            </td>
                            <td className="px-5 py-4">
                              {d.logo_url ? (
                                <img
                                  src={d.logo_url.startsWith('/') ? `${BACKEND_URL}${d.logo_url}` : d.logo_url}
                                  alt="Logo"
                                  className="h-8 w-8 object-contain rounded border border-slate-100 bg-slate-50"
                                  onError={e => { e.target.style.display = 'none'; }}
                                />
                              ) : <span className="text-slate-300 text-xs">—</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {!domainsLoading && (
                <div className="px-5 py-3 border-t border-slate-100 bg-slate-50">
                  <p className="text-xs text-slate-400">Showing {filtered.length} of {domains.length} partners</p>
                </div>
              )}
            </div>
          </>
        )}

        {/* ═══ DOMAIN REGISTRATIONS ═══ */}
        {activeTab === 'registrations' && (
          <div className="space-y-4">
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search domain, partner, client..."
                value={regSearch}
                onChange={e => setRegSearch(e.target.value)}
                className="pl-9"
                data-testid="reg-search-input"
              />
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              {regLoading ? (
                <div className="flex items-center justify-center h-48">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                </div>
              ) : filteredReg.length === 0 ? (
                <div className="text-center py-16 text-slate-400">
                  <ShoppingCart className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No domain registrations found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="registrations-table">
                    <thead>
                      <tr className="border-b border-slate-100 bg-slate-50">
                        {['#', 'Domain', 'Partner', 'Client', 'Yrs', 'Amount', 'Commission', 'RC Order', 'Date', 'Status'].map(h => (
                          <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredReg.map((o, idx) => (
                        <tr key={o.id || idx} data-testid={`reg-row-${idx}`} className="hover:bg-slate-50 transition-colors">
                          <td className="px-4 py-3 text-slate-400 text-xs">{idx + 1}</td>
                          <td className="px-4 py-3 font-medium text-slate-800 font-mono text-xs">{o.domain}</td>
                          <td className="px-4 py-3">
                            <p className="text-slate-700 text-xs">{o.partner_name || '—'}</p>
                            <p className="text-slate-400 text-[11px]">{o.partner_email}</p>
                          </td>
                          <td className="px-4 py-3">
                            {o.client_name || o.client_email
                              ? <span className="text-slate-700 text-xs">{o.client_name || o.client_email}</span>
                              : <span className="text-slate-400 text-xs">Own Use</span>}
                          </td>
                          <td className="px-4 py-3 text-slate-600 text-xs">{o.years}</td>
                          <td className="px-4 py-3 font-semibold text-slate-700 text-xs">{fmt(o.selling_price)}</td>
                          <td className="px-4 py-3 text-green-600 font-medium text-xs">{fmt(o.commission)}</td>
                          <td className="px-4 py-3 text-slate-400 text-[11px] font-mono">{o.rc_order_id || '—'}</td>
                          <td className="px-4 py-3 text-slate-500 text-xs">{fmtDate(o.created_at)}</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium
                              ${o.status === 'active' || o.status === 'renewed'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-slate-100 text-slate-500'}`}>
                              {o.type === 'renewal' ? 'Renewal' : o.status || 'active'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {!regLoading && (
                <div className="px-5 py-3 border-t border-slate-100 bg-slate-50">
                  <p className="text-xs text-slate-400">
                    {filteredReg.length} registration{filteredReg.length !== 1 ? 's' : ''} &bull;
                    Total Revenue: {fmt(filteredReg.reduce((s, o) => s + (o.selling_price || 0), 0))} &bull;
                    Total Commission: {fmt(filteredReg.reduce((s, o) => s + (o.commission || 0), 0))}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══ SERVER IP / RC WHITELIST ═══ */}
        {activeTab === 'serverIp' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-amber-50 p-3 rounded-xl">
                  <Server className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-slate-800">Server Outbound IP</h2>
                  <p className="text-sm text-slate-500">Add this IP to ResellerClub API whitelist for domain operations</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 font-mono text-slate-800 text-sm" data-testid="server-ip-display">
                  {ipLoading ? 'Fetching…' : (serverIp || 'Click refresh to fetch')}
                </div>
                <Button
                  onClick={fetchServerIp}
                  disabled={ipLoading}
                  data-testid="fetch-server-ip-btn"
                  className="gap-2"
                >
                  <RefreshCw className={`w-4 h-4 ${ipLoading ? 'animate-spin' : ''}`} />
                  Refresh IP
                </Button>
                {serverIp && serverIp !== 'Unable to fetch' && serverIp !== 'Unknown' && (
                  <Button
                    variant="outline"
                    onClick={() => { navigator.clipboard.writeText(serverIp); toast.success('IP copied!'); }}
                    data-testid="copy-ip-btn"
                    className="gap-2"
                  >
                    Copy
                  </Button>
                )}
              </div>

              <div className="mt-6 space-y-3 text-sm text-slate-600">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="font-semibold text-amber-800 mb-2">Important: ResellerClub IP Whitelist</p>
                  <ul className="list-disc list-inside space-y-1 text-amber-700 text-sm">
                    <li>ResellerClub allows only <strong>3 IPs</strong> in the whitelist</li>
                    <li>This server's outbound IP can change on pod restart or scaling</li>
                    <li>After each deployment, re-check and update the IP in RC if needed</li>
                    <li>For stable IP, consider using a <strong>NAT Gateway</strong> or static egress IP solution</li>
                  </ul>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <p className="font-semibold text-slate-700 mb-2">How to whitelist in ResellerClub:</p>
                  <ol className="list-decimal list-inside space-y-1.5 text-slate-600 text-sm">
                    <li>Login to ResellerClub reseller panel → <strong>Settings → API</strong></li>
                    <li>Go to <strong>"Whitelist IP Addresses"</strong> section</li>
                    <li>Add the IP shown above (remove oldest if 3 slots are full)</li>
                    <li>Save changes → test domain search in partner panel</li>
                  </ol>
                  <a
                    href="https://manage.resellerclub.com/servlet/ManageResellerServlet?cmd=ResellerApiAccess"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-3 text-blue-600 hover:underline text-sm font-medium"
                    data-testid="rc-settings-link"
                  >
                    Open ResellerClub API Settings →
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </DashboardLayout>
  );
}
